import { Box, Paper, Stack, Typography, Alert, Link } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router";
import { useState } from "react";
import { FormField, LoadingButton } from "@safrater/ui";
import { api } from "../../api/client";

/**
 * Esqueci minha senha — fluxo em 2 passos integrando o OTP por e-mail:
 *   1. informa o e-mail → backend envia um código TOTP-Khori (mesmo do login OTP)
 *   2. informa o código + nova senha → backend valida o OTP e redefine a senha
 * Sem link/token de e-mail: reusa o mecanismo de OTP já existente.
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"request" | "reset" | "done">("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const mismatch = !!confirm && next !== confirm;
  const canReset = !!code && next.length >= 10 && next === confirm && !busy;

  async function sendCode() {
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/otp/send", { email });
      setStep("reset");
      setInfo("Se o e-mail estiver cadastrado, enviamos um código de 6 dígitos. Ele é válido por 3 minutos.");
    } catch {
      setError("Não foi possível enviar o código. Tente novamente.");
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/password/reset-otp", { email, code, newPassword: next });
      setStep("done");
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? "Não foi possível redefinir a senha. Verifique o código.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 440, width: "100%" }}>
        <Typography variant="h5" gutterBottom>Esqueci minha senha</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {info && step === "reset" && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}

        {step === "request" && (
          <Stack component="form" spacing={2} onSubmit={(e) => { e.preventDefault(); if (email && !busy) void sendCode(); }}>
            <Typography variant="body2" color="text.secondary">
              Informe seu e-mail. Vamos enviar um código de 6 dígitos para você redefinir a senha.
            </Typography>
            <FormField label="E-mail" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <LoadingButton type="submit" variant="contained" size="large" fullWidth loading={busy} disabled={!email}>
              Enviar código
            </LoadingButton>
            <Stack direction="row" justifyContent="flex-end">
              <Link component={RouterLink} to="/login">Voltar para o login</Link>
            </Stack>
          </Stack>
        )}

        {step === "reset" && (
          <Stack component="form" spacing={2} onSubmit={(e) => { e.preventDefault(); if (canReset) void reset(); }}>
            <Typography variant="body2" color="text.secondary">
              Digite o código enviado para <b>{email}</b> e escolha uma nova senha.
            </Typography>
            <FormField
              label="Código" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", maxLength: 6, autoComplete: "one-time-code" }}
            />
            <FormField label="Nova senha" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)}
              helperText="Mínimo de 10 caracteres" />
            <FormField label="Confirmar nova senha" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              errorMessage={mismatch ? "As senhas não conferem" : undefined} />
            <LoadingButton type="submit" variant="contained" size="large" fullWidth loading={busy} disabled={!canReset}>
              Redefinir senha
            </LoadingButton>
            <Stack direction="row" justifyContent="space-between">
              <Link component="button" type="button" onClick={() => void sendCode()}>Reenviar código</Link>
              <Link component="button" type="button" onClick={() => { setStep("request"); setCode(""); setInfo(null); setError(null); }}>
                Mudar e-mail
              </Link>
            </Stack>
          </Stack>
        )}

        {step === "done" && (
          <Stack spacing={2}>
            <Alert severity="success">Senha redefinida com sucesso. Você já pode entrar com a nova senha.</Alert>
            <LoadingButton variant="contained" size="large" fullWidth onClick={() => navigate("/login")}>
              Ir para o login
            </LoadingButton>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
